import { useEffect, useMemo, useRef } from 'react';
import type { GeoJSONSource, MapMouseEvent } from 'mapbox-gl';
import { UMMap, UMMapControls, type UMMapHandle } from '../../urbanmap/_components';
import { EMPTY, buildingsFC, drawFC, perimeterLayers, ringFC } from './mapLayers';
import type { LngLat, PlanBuilding } from './types';

interface Props {
  buildings: PlanBuilding[];
  included: Set<string>;
  bounds: [LngLat, LngLat];
  outlines: LngLat[][];
  drawing: boolean;
  points: LngLat[];
  closed: boolean;
  onAddPoint: (p: LngLat) => void;
  /** Termine le polygone (double-clic ou clic sur le premier sommet), avec les sommets nettoyés. */
  onFinish: (points: LngLat[]) => void;
}

const SNAP_PX = 10;

/** Carte de l'assistant : bâtiments UM du référentiel, inclus/exclus, et dessin d'un contour. */
export function PerimeterMap({ buildings, included, bounds, outlines, drawing, points, closed, onAddPoint, onFinish }: Props) {
  const mapRef = useRef<UMMapHandle>(null);
  const latest = useRef({ drawing, points, onAddPoint, onFinish });
  latest.current = { drawing, points, onAddPoint, onFinish };

  const layers = useMemo(() => perimeterLayers(), []);
  const sources = useMemo(() => ({
    buildings: buildingsFC(buildings, (b) => ({ inc: included.has(b.id) ? 1 : 0 })),
    outline: ringFC(outlines),
    draw: drawFC(points, closed),
    rubber: EMPTY,
  }), [buildings, included, outlines, points, closed]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return undefined;
    const rubber = (data: GeoJSON.FeatureCollection) => (map.getSource('rubber') as GeoJSONSource | undefined)?.setData(data);

    const click = (e: MapMouseEvent) => {
      const { drawing: on, points: pts } = latest.current;
      if (!on) return;
      if (pts.length >= 3) {
        const first = map.project(pts[0]);
        if (Math.hypot(first.x - e.point.x, first.y - e.point.y) < SNAP_PX) { latest.current.onFinish(pts); rubber(EMPTY); return; }
      }
      latest.current.onAddPoint([e.lngLat.lng, e.lngLat.lat]);
    };
    const dblclick = (e: MapMouseEvent) => {
      if (!latest.current.drawing) return;
      e.preventDefault();
      // The two clicks of the double-click added vertices: keep a single one at that spot.
      const pts = [...latest.current.points];
      while (pts.length > 1) {
        const a = map.project(pts[pts.length - 1]);
        const b = map.project(pts[pts.length - 2]);
        if (Math.hypot(a.x - b.x, a.y - b.y) >= SNAP_PX) break;
        pts.pop();
      }
      latest.current.onFinish(pts);
      rubber(EMPTY);
    };
    const move = (e: MapMouseEvent) => {
      const { drawing: on, points: pts } = latest.current;
      if (!on || pts.length === 0) return;
      rubber({ type: 'FeatureCollection', features: [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: [pts[pts.length - 1], [e.lngLat.lng, e.lngLat.lat]] } }] });
    };
    map.on('click', click);
    map.on('dblclick', dblclick);
    map.on('mousemove', move);
    return () => {
      map.off('click', click);
      map.off('dblclick', dblclick);
      map.off('mousemove', move);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    if (drawing) {
      map.doubleClickZoom.disable();
    } else {
      map.doubleClickZoom.enable();
      (map.getSource('rubber') as GeoJSONSource | undefined)?.setData(EMPTY);
    }
  }, [drawing]);

  return (
    <div className={drawing ? 'pc-drawing' : undefined} style={{ position: 'relative', width: '100%', height: '100%' }}>
      <style>{'.pc-drawing .mapboxgl-canvas-container, .pc-drawing .mapboxgl-canvas{cursor:crosshair!important}'}</style>
      <UMMap ref={mapRef} sources={sources} layers={layers} initialBounds={bounds} />
      <UMMapControls basemap="light" availableBasemaps={['light']} onZoomIn={() => mapRef.current?.zoomIn()} onZoomOut={() => mapRef.current?.zoomOut()} />
    </div>
  );
}
